import React from "react";
import {
  DocumentDuplicateIcon,
  UsersIcon,
  UserGroupIcon,
  HeartIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingLibraryIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";

// FIX: destructure `data` (what VotersPage passes) and rename it to `dashboardData`
// Previously this was { dashboardData } which never matched the parent's data={...} prop,
// causing dashboardData to always be undefined and the component to return null.
const DemographicsDashboard = ({ data: dashboardData, showDetails, toggleDetails }) => {
  if (!dashboardData) return null;

  const demographics = dashboardData.voter_demographics || {};

  const precinctStats = demographics.voters_by_precincts || [];
  const genderStats = demographics.gender_distribution || [];
  const ageGroups = demographics.age_groups || {};
  const occupationStats = demographics.occupation_distribution || [];
  const civilStats = demographics.civil_status_distribution || [];
  const purokStats = demographics.purok_distribution || [];

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow mb-6">
        <button
          onClick={toggleDetails}
          className="w-full px-6 py-4 flex items-center justify-between text-left transition-all duration-200 rounded-lg"
        >
          <div className="flex items-center">
            <DocumentDuplicateIcon className="w-6 h-6 mr-3 text-blue-600" />
            <div>
              <h4 className="text-lg font-semibold text-gray-900">
                Voter Demographics
              </h4>
              <p className="text-sm text-gray-600">
                Breakdown by age, gender, and civil status, etc.
              </p>
            </div>
          </div>
          {showDetails ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </button>

        {showDetails && (
          <div className="space-y-6 p-4 animate-in slide-in-from-top duration-300">
            {/* First Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Voters by Precinct */}
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BuildingLibraryIcon className="w-5 h-5 mr-2 text-green-600" />
                  Voters by Precinct
                </h3>
                <div className="space-y-1">
                  {(precinctStats.length > 0 ? precinctStats : [{ name: 0, total_voters: 0 }]).map((p, index) => (
                    <div
                      key={p.id || p.name || index}
                      className="flex justify-between items-center"
                    >
                      <span className="text-sm text-gray-600">
                        {p.name || p.precinct || 0}
                      </span>
                      <span className="text-md font-semibold text-green-600">
                        {p.total_voters ?? p.count ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gender Distribution */}
              <div className="bg-teal-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UsersIcon className="w-5 h-5 mr-2 text-teal-600" />
                  Gender Distribution
                </h3>
                <div className="space-y-4">
                  {(genderStats.length > 0 ? genderStats : [{ gender: 0, count: 0, percentage: 0 }]).map(
                    (g, index) => {
                      const gender = g.gender?.toLowerCase?.() || "";
                      const isMale = gender === "male";
                      const isFemale = gender === "female";

                      const textColor = isMale
                        ? "text-green-600"
                        : isFemale
                        ? "text-pink-600"
                        : "text-gray-600";

                      const barColor = isMale
                        ? "bg-green-600"
                        : isFemale
                        ? "bg-pink-600"
                        : "bg-gray-400";

                      return (
                        <React.Fragment key={g.gender || g.id || index}>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 capitalize">
                              {g.gender || 0} Voters
                            </span>
                            <span
                              className={`text-md font-bold tabular-nums ${textColor}`}
                            >
                              {g.count ?? 0}{" "}
                              <span className="text-xs font-normal text-gray-700">
                                ({g.percentage ?? 0}%)
                              </span>
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${barColor}`}
                              style={{ width: `${g.percentage ?? 0}%` }}
                            ></div>
                          </div>
                        </React.Fragment>
                      );
                    }
                  )}
                </div>
              </div>

              {/* Age Group */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserGroupIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Age Group
                </h3>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Youth (15–17)</span>
                    <span className="text-md font-bold text-blue-600">
                      {ageGroups.youth ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      Early Adults (18–24)
                    </span>
                    <span className="text-md font-bold text-blue-600">
                      {ageGroups.early_adults ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      Regular Voters (25–59)
                    </span>
                    <span className="text-md font-bold text-blue-600">
                      {ageGroups.regular_voters ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Seniors (60+)</span>
                    <span className="text-md font-bold text-blue-600">
                      {ageGroups.senior ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">PWD</span>
                    <span className="text-md font-bold text-blue-600">
                      {ageGroups.pwd ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Illiterate</span>
                    <span className="text-md font-bold text-blue-600">
                      {ageGroups.illitirate ?? 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Occupation Distribution */}
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BriefcaseIcon className="w-5 h-5 mr-2 text-purple-600" />
                  Occupation Distribution
                </h3>
                <div className="space-y-1">
                  {(occupationStats.length > 0 ? occupationStats : [{ status: 0, count: 0, percentage: 0 }]).map(
                    (o, index) => (
                      <div
                        key={o.status || index}
                        className="flex justify-between items-center"
                      >
                        <span className="text-sm text-gray-600">
                          {o.status || 0}
                        </span>
                        <span className="text-md font-bold text-purple-600">
                          {o.count ?? 0}{" "}
                          <span className="text-xs font-normal text-gray-700">
                            ({o.percentage ?? 0}%)
                          </span>
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Civil Status Distribution */}
              <div className="bg-red-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <HeartIcon className="w-5 h-5 mr-2 text-red-600" />
                  Civil Status Distribution
                </h3>
                <div className="space-y-1">
                  {(civilStats.length > 0 ? civilStats : [{ civil_status: 0, count: 0, percentage: 0 }]).map(
                    (c, index) => (
                      <div
                        key={c.civil_status || index}
                        className="flex justify-between items-center"
                      >
                        <span className="text-sm text-gray-600 capitalize">
                          {c.civil_status || 0}
                        </span>
                        <span className="text-md font-bold text-red-600">
                          {c.count ?? 0}{" "}
                          <span className="text-xs font-normal text-gray-700">
                            ({c.percentage ?? 0}%)
                          </span>
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Purok/Zone Distribution */}
              <div className="bg-orange-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPinIcon className="w-5 h-5 mr-2 text-orange-600" />
                  Purok/Zone Distribution
                </h3>
                <div className="space-y-1">
                  {(purokStats.length > 0 ? purokStats : [{ purok_zone: 0, count: 0, percentage: 0 }]).map(
                    (p, index) => (
                      <div
                        key={p.purok_zone || index}
                        className="flex justify-between items-center"
                      >
                        <span className="text-sm text-gray-600">
                          {p.purok_zone || 0}
                        </span>
                        <span className="text-md font-bold text-orange-600">
                          {p.count ?? 0}{" "}
                          <span className="text-xs font-normal text-gray-700">
                            ({p.percentage ?? 0}%)
                          </span>
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemographicsDashboard;